import * as React from "react";
import classNames from "classnames";
import { theme } from "styles/theme";
import { Image as ImageIcon } from "components/icons/dashboard";
import { $, merge } from "glamor";

import DirectS3Uploader from "components/form/direct-s3-uploader";
import CircularProgress from "material-ui/CircularProgress";
import { format } from "lib/globalize-util";
import { DragMessage } from "components/upload/components";
import type { IScope } from "reducers/scope";
import { useSetLineSheetLogo } from "./hooks/linesheets";
interface IImageUploaderProps {
    scope: IScope;
    type: string;
    uploadURL: string;
    imageUrl: string;
    onSuccessShowSnack: (response: object) => void;
    onError: (response: object) => void;
    onBrandChange: (newValue: object) => void;
}

export enum LogoTypes {
    Dashboard = "dashboard_logo_image",
    Product = "missing_logo_image",
    Pdf = "pdf_logo_image",
    Linesheets = "linesheets",
}

export const ImageUploader: React.FC<IImageUploaderProps> = ({
    scope,
    type,
    imageUrl,
    uploadURL,
    onSuccessShowSnack,
    onBrandChange,
    onError,
}) => {
    const styles = React.useMemo(
        () =>
            merge(
                $(" .img-logo", {
                    maxWidth: "100%",
                }),
                $(" .instructions", {
                    textAlign: "center",
                    marginTop: "10%",
                }),
                $(" .image-container", {
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }),
                $(" .dragLabel", {
                    fontSize: 14,
                    marginBottom: 10,
                    textAlign: "center",
                    lineHeight: "1.8em",
                    color: theme.palette.darkGrey,
                }),
                $(" .dashboard_logo_image", { width: 160, height: 160 }),
                $(" .missing_logo_image", { width: 163, height: 257 }),
                $(" .pdf_logo_image", { width: 340, height: 55 }),
                $(" .fullSquare", { width: "100%", height: "100%" })
            ),
        []
    );

    const [lineSheetsImage, setLineSheetsImage] = React.useState<string>();
    const { data, isFetching } = useSetLineSheetLogo(type, lineSheetsImage, { enabled: lineSheetsImage });
    const [isUploading, setUploading] = React.useState<boolean>(false);
    const [validImageUrl, setValidImageUrl] = React.useState<string>(imageUrl);

    React.useEffect(() => {
        if (data) {
            const key = `modules.base.branding.${type}`;
            const body = data.modules?.base.branding && data.modules.base.branding[type];
            onBrandChange({ key, body });
            setLineSheetsImage(null);
        }
    }, [data, onBrandChange]); // eslint-disable-line react-hooks/exhaustive-deps

    // Updating URL when new image uploads
    React.useEffect(() => {
        setValidImageUrl(imageUrl);
    }, [imageUrl]);

    const handleImageError = React.useCallback(() => {
        setValidImageUrl("");
    }, []);

    const handleError = (err) => onError(err);

    const handleLoading = React.useCallback((isLoading: boolean) => setUploading(isLoading), []);

    const handleSuccess = React.useCallback(
        ({ success: [[body]], originals: [OriginalImage] }) => {
            const key = `settings.${type}`;

            if (type === LogoTypes.Linesheets) {
                setLineSheetsImage(`${body}${OriginalImage.ext}`);
            } else {
                onBrandChange({ key, body });
            }
            onSuccessShowSnack({ message: format("Image updated successfully") });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [type]
    );

    return (
        <div {...styles}>
            <div className={classNames("image-container", type, { fullSquare: !validImageUrl })}>
                <DirectS3Uploader
                    onUploadError={handleError}
                    uploadCallback={handleSuccess}
                    uploadUrl={uploadURL}
                    accept="image/jpeg, image/jpg, image/png"
                    showSelectButton={false}
                    uploadInstructions={format("Drag or click image to upload")}
                    scope={scope}
                    disabled={isUploading || isFetching}
                    onLoading={handleLoading}
                    isLegacy={true}
                    logoType={type}
                >
                    {validImageUrl && !(isUploading || isFetching) && (
                        <img className="img-logo" src={`//${validImageUrl}`} onError={handleImageError} />
                    )}
                    {(isUploading || isFetching) && <CircularProgress />}

                    {!validImageUrl && !lineSheetsImage && !(isUploading || isFetching) && (
                        <div className="instructions">
                            <React.Fragment>
                                <ImageIcon style={{ width: 80, height: 80, color: theme.palette.grey }} />
                                <div className="dragLabel">
                                    <DragMessage
                                        path={"Drag your file here |br| or |span|click here|span| to select"}
                                    />
                                </div>
                            </React.Fragment>
                        </div>
                    )}
                </DirectS3Uploader>
            </div>
        </div>
    );
};
